import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Box,
  Button,
  Divider,
  Chip,
  TextField,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useLocation } from "react-router-dom";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";

import { BaseUrl } from "../BaseUrl";

const HEADER_H = 72;
const FOOTER_H = 56;

const PAPER_KEY = "rupioo_costing_paper_orders_v2"; // ✅ must match SecondPage
const ORDERS_API = `${BaseUrl}/api/orders`;
const PRODUCTS_API = `${BaseUrl}/api/products`;

const UI = {
  pageBg: "#EAEDED",
  headerBg: "#131921",
  amazonOrange: "#FF9900",
  amazonOrangeDeep: "#F08804",
  linkBlue: "#007185",
  text: "#111",
  sub: "#565959",
  border: "#D5D9D9",
  cardBg: "#fff",
  soft: "#F7F8F8",
};

const parsed = (v) => parseFloat(v) || 0;

function safeReadPapers() {
  try {
    const raw = localStorage.getItem(PAPER_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    return [];
  }
}
function safeWritePapers(arr) {
  try {
    localStorage.setItem(PAPER_KEY, JSON.stringify(arr));
  } catch (e) {}
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch (e) {
    return iso;
  }
}

/* ------------------ INPUT/OUTPUT fields ------------------ */
const INPUT_FIELDS = [
  { key: "orderSheetL", label: "Order Sheet Length" },
  { key: "orderSheetB", label: "Order Sheet Breadth" },
  { key: "orderSheetGsm", label: "Order Sheet GSM" },
  { key: "quantity", label: "Sheet Quantity" },
  { key: "piecePerSheet", label: "Piece Per Sheet" },
  { key: "rate", label: "Rate" },
  { key: "printingPer1000", label: "Printing Charge (Per 1000)" },
  { key: "plateCharge", label: "Plate Charge" },
  { key: "cuttingPer1000", label: "Cutting Charge (Per 10000)" },
  { key: "conversionCost", label: "Conversion Cost (%)" },
  { key: "miscCost", label: "Miscellaneous Cost (%)" },
  { key: "salePrice", label: "Sale Price" },
  { key: "gstRate", label: "GST Rate (%)" },
];

const OUTPUT_FIELDS = [
  { key: "wtPerSheet", label: "Weight / Sheet" },
  { key: "totalWeight", label: "Total Weight" },
  { key: "amount", label: "Sheet Amount" },
  { key: "totalPrintingCost", label: "Printing Cost" },
  { key: "totalCuttingCharges", label: "Cutting Charges" },
  { key: "miscellaneousCharges", label: "Misc Charges" },
  { key: "totalConversionCost", label: "Conversion Cost" },
  { key: "totalCost", label: "Total Basic Cost" },
  { key: "totalQuantity", label: "Total Quantity" },
  { key: "costPerPcs", label: "Cost / Piece" },
  { key: "gstOnSale", label: "GST on Sale" },
  { key: "profitPerPc", label: "Profit / Piece" },
];

function calculateOutputs(formData) {
  const {
    orderSheetL,
    orderSheetB,
    orderSheetGsm,
    rate,
    printingPer1000,
    plateCharge,
    cuttingPer1000,
    conversionCost,
    miscCost,
    salePrice,
    gstRate,
    quantity,
    piecePerSheet,
  } = formData || {};

  const wtPerSheet =
    (parsed(orderSheetL) * parsed(orderSheetB) * parsed(orderSheetGsm)) /
    10000000;

  const totalWeight = wtPerSheet * parsed(quantity);
  const amount = totalWeight * parsed(rate);

  const totalPrintingCost =
    (parsed(quantity) / 1000) * parsed(printingPer1000) + parsed(plateCharge);

  const totalCuttingCharges =
    (parsed(quantity) / 1000) * parsed(cuttingPer1000);

  const miscellaneousCharges = (amount * parsed(miscCost)) / 100;

  const gstOnSale =
    (parsed(salePrice) * parsed(gstRate)) / (100 + parsed(gstRate));

  const totalConversionCost = (amount * parsed(conversionCost)) / 100;

  const totalCost =
    amount +
    totalPrintingCost +
    totalCuttingCharges +
    miscellaneousCharges +
    totalConversionCost;

  const totalQuantity = parsed(quantity) * parsed(piecePerSheet);
  const costPerPcs = totalCost / (totalQuantity || 1);
  const profitPerPc = parsed(salePrice) - (costPerPcs + gstOnSale);

  return {
    wtPerSheet: wtPerSheet.toFixed(4),
    totalWeight: totalWeight.toFixed(2),
    amount: amount.toFixed(2),
    totalPrintingCost: totalPrintingCost.toFixed(2),
    totalCuttingCharges: totalCuttingCharges.toFixed(2),
    miscellaneousCharges: miscellaneousCharges.toFixed(2),
    totalConversionCost: totalConversionCost.toFixed(4),
    totalCost: totalCost.toFixed(2),
    totalQuantity: totalQuantity.toFixed(2),
    costPerPcs: costPerPcs.toFixed(4),
    gstOnSale: gstOnSale.toFixed(2),
    profitPerPc: profitPerPc.toFixed(4),
    piecePerSheet: parsed(piecePerSheet).toFixed(4),
  };
}

/* ✅ GST helpers for OrderList page (as you requested) */
function gstAmountFromItem(it) {
  const base = parsed(it?.totalWeight) * parsed(it?.rate);
  const gstRate = parsed(it?.gstRate);
  return (base * gstRate) / 100;
}
function amountWithGstFromItem(it) {
  const base = parsed(it?.totalWeight) * parsed(it?.rate);
  return base + gstAmountFromItem(it);
}

/* ------------------ update Product master ------------------ */
async function updateProductMaster(productId, formData, outputs) {
  if (!productId) return;

  const patch = {
    orderSheetL: formData?.orderSheetL,
    orderSheetB: formData?.orderSheetB,
    orderSheetGsm: formData?.orderSheetGsm,
    quantity: formData?.quantity,
    rate: formData?.rate,
    printingPer1000: formData?.printingPer1000,
    plateCharge: formData?.plateCharge,
    cuttingPer1000: formData?.cuttingPer1000,
    conversionCost: formData?.conversionCost,
    miscCost: formData?.miscCost,
    salePrice: formData?.salePrice,
    gstRate: formData?.gstRate,
    piecePerSheet: formData?.piecePerSheet,

    totalWeight: outputs?.totalWeight,
    amount: outputs?.amount,
    totalCost: outputs?.totalCost,
    costPerPcs: outputs?.costPerPcs,
  };

  try {
    const getRes = await axios.get(`${PRODUCTS_API}/${productId}`);
    const existing = getRes.data || {};
    const merged = { ...existing, ...patch };
    await axios.put(`${PRODUCTS_API}/${productId}`, merged);
    return;
  } catch (e) {
    try {
      await axios.put(`${PRODUCTS_API}/${productId}`, patch);
    } catch (e2) {
      console.error("Product master update failed:", e2);
    }
  }
}

/* ------------------ row fields UI ------------------ */
function RowFields({ title, fields, values, onChange, readOnly }) {
  return (
    <Box sx={{ mt: 1.4 }}>
      <Typography sx={{ fontWeight: 1000, color: UI.text }}>{title}</Typography>

      <Box
        sx={{
          mt: 1,
          display: "flex",
          gap: 1,
          flexWrap: "nowrap",
          overflowX: "auto",
          pb: 0.5,
          "&::-webkit-scrollbar": { height: 8 },
          "&::-webkit-scrollbar-thumb": {
            background: "#cfd3d6",
            borderRadius: 999,
          },
          "&::-webkit-scrollbar-track": { background: "transparent" },
        }}
      >
        {fields.map(({ key, label }) => (
          <Box
            key={key}
            sx={{
              flex: "0 0 auto",
              width: 170,
              minWidth: 170,
              borderRadius: 1.4,
              border: `1px solid ${UI.border}`,
              background: "#fff",
              p: 1.1,
            }}
          >
            <Typography sx={{ fontSize: 12, color: UI.sub, fontWeight: 900 }}>
              {label}
            </Typography>

            <TextField
              size="small"
              fullWidth
              value={values?.[key] ?? ""}
              onChange={(e) => onChange(key, e.target.value)}
              InputProps={{ readOnly: !!readOnly }}
              sx={{ mt: 0.8, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function OrderList() {
  const location = useLocation();

  const [orders, setOrders] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const [papers, setPapers] = useState([]);
  const [openPaperId, setOpenPaperId] = useState(null);

  const [openOrderId, setOpenOrderId] = useState(null);

  // editing paper item
  const [paperEdit, setPaperEdit] = useState(null); // { paperId, idx }
  const [paperEditForm, setPaperEditForm] = useState({});
  const paperEditOutputs = useMemo(
    () => calculateOutputs(paperEditForm),
    [paperEditForm],
  );

  // editing submitted item
  const [editOpen, setEditOpen] = useState(false);
  const [editOrderId, setEditOrderId] = useState(null);
  const [editItemIndex, setEditItemIndex] = useState(-1);
  const [editHeader, setEditHeader] = useState({ orderNo: "", partyName: "" });
  const [editItemMeta, setEditItemMeta] = useState({
    productId: "",
    name: "",
    boardQuality: "",
    category: "",
  });
  const [editForm, setEditForm] = useState({});
  const editOutputs = useMemo(() => calculateOutputs(editForm), [editForm]);

  useEffect(() => {
    refreshOrders();
    setPapers(safeReadPapers());
  }, []);

  // ✅ auto open paper after redirect (optional but helpful)
  useEffect(() => {
    const pid = location?.state?.openPaperId;
    if (pid) setOpenPaperId(pid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state?.openPaperId]);

  const refreshOrders = () => {
    axios
      .get(ORDERS_API)
      .then((res) => setOrders(res.data || []))
      .catch((err) => console.error("Error fetching orders:", err));
  };

  /* --------------------- PAPER helpers --------------------- */
  const updatePaper = (paperId, patch) => {
    setPapers((prev) => {
      const next = prev.map((p) =>
        p.paperId === paperId ? { ...p, ...patch } : p,
      );
      safeWritePapers(next);
      return next;
    });
  };

  const deletePaper = (paperId) => {
    setPapers((prev) => {
      const next = prev.filter((p) => p.paperId !== paperId);
      safeWritePapers(next);
      if (openPaperId === paperId) setOpenPaperId(null);
      if (paperEdit?.paperId === paperId) cancelPaperItemEdit();
      return next;
    });
  };

  const recomputePaperTotals = (items = []) => {
    let amount = 0;
    let totalWeight = 0;
    let totalCost = 0;

    for (const it of items) {
      amount += parseFloat(it?.outputs?.amount || 0);
      totalWeight += parseFloat(it?.outputs?.totalWeight || 0);
      totalCost += parseFloat(it?.outputs?.totalCost || 0);
    }

    return {
      amount,
      totalWeight,
      totalCost,
      itemsCount: items.length,
    };
  };

  const startPaperItemEdit = (paperId, idx) => {
    const p = papers.find((x) => x.paperId === paperId);
    const it = p?.items?.[idx];
    if (!it) return;

    setPaperEdit({ paperId, idx });
    setPaperEditForm({ ...(it.formData || {}) });
  };

  const cancelPaperItemEdit = () => {
    setPaperEdit(null);
    setPaperEditForm({});
  };

  const savePaperItemEdit = async () => {
    if (!paperEdit) return;

    const { paperId, idx } = paperEdit;

    let productIdToSync = "";
    let inputsToSync = null;
    let outputsToSync = null;

    setPapers((prev) => {
      const next = prev.map((p) => {
        if (p.paperId !== paperId) return p;

        const items = [...(p.items || [])];
        const old = items[idx];
        if (!old) return p;

        const outputs = calculateOutputs(paperEditForm);

        productIdToSync = old.productId || "";
        inputsToSync = { ...paperEditForm };
        outputsToSync = { ...outputs };

        items[idx] = {
          ...old,
          formData: { ...paperEditForm },
          outputs,
        };

        const totals = recomputePaperTotals(items);
        return { ...p, items, totals };
      });

      safeWritePapers(next);
      return next;
    });

    try {
      if (productIdToSync) {
        await updateProductMaster(productIdToSync, inputsToSync, outputsToSync);
      }
    } catch (e) {
      console.error("Draft edit -> product sync failed:", e);
    }

    cancelPaperItemEdit();
  };

  /* --------------------- submit paper -> backend --------------------- */
  const submitPaper = async (paper) => {
    if (!paper?.items?.length) return;

    const itemsPayload = (paper.items || []).map((it) => {
      const fd = it.formData || {};
      const out = it.outputs || {};

      return {
        productId: it.productId || "",
        category: it.category || it.catageory || it.Catageory || "", // ✅ category fix
        boardQuality: it.boardQuality || "",
        name: it.name || "",

        orderSheetL: parsed(fd.orderSheetL),
        orderSheetB: parsed(fd.orderSheetB),
        orderSheetGsm: parsed(fd.orderSheetGsm),
        quantity: parsed(fd.quantity),
        piecePerSheet: parsed(fd.piecePerSheet),
        rate: parsed(fd.rate),
        printingPer1000: parsed(fd.printingPer1000),
        plateCharge: parsed(fd.plateCharge),
        cuttingPer1000: parsed(fd.cuttingPer1000),
        conversionCost: parsed(fd.conversionCost),
        miscCost: parsed(fd.miscCost),
        salePrice: parsed(fd.salePrice),
        gstRate: parsed(fd.gstRate),

        wtPerSheet: parsed(out.wtPerSheet),
        totalWeight: parsed(out.totalWeight),
        amount: parsed(out.amount),
        totalPrintingCost: parsed(out.totalPrintingCost),
        totalCuttingCharges: parsed(out.totalCuttingCharges),
        miscellaneousCharges: parsed(out.miscellaneousCharges),
        totalConversionCost: parsed(out.totalConversionCost),
        totalCost: parsed(out.totalCost),
        totalQuantity: parsed(out.totalQuantity),
        costPerPcs: parsed(out.costPerPcs),
        gstOnSale: parsed(out.gstOnSale),
        profitPerPc: parsed(out.profitPerPc),
      };
    });

    const totals = recomputePaperTotals(paper.items || []);

    try {
      await axios.post(ORDERS_API, {
        orderNo: paper.orderNo || "",
        partyName: paper.partyName || "",
        orderDate: paper.orderDate || new Date().toISOString(),
        totals: {
          amount: totals.amount,
          totalWeight: totals.totalWeight,
          totalCost: totals.totalCost,
          itemsCount: totals.itemsCount,
        },
        items: itemsPayload,
      });

      deletePaper(paper.paperId);
      refreshOrders();
    } catch (e) {
      console.error("Submit failed:", e);
    }
  };

  /* --------------------- submitted delete selected --------------------- */
  const deleteSelectedSubmitted = async () => {
    await axios.delete(`${BaseUrl}/api/orders/bulk-delete`, {
      data: { ids: selectedIds },
    });
    setSelectedIds([]);
    refreshOrders();
  };

  /* ✅ Only selected orders will export */
  const getSelectedOrders = () => {
    const sel = orders.filter((o) => selectedIds.includes(o._id));
    if (!sel.length) {
      alert("Please select at least 1 submitted order to download.");
      return null;
    }
    return sel;
  };

  /* --------------------- Excel / PDF (selected only) --------------------- */
  const downloadExcel = () => {
    const selectedOrders = getSelectedOrders();
    if (!selectedOrders) return;

    const rows = [];
    for (const o of selectedOrders) {
      const items = Array.isArray(o.items) ? o.items : [];
      for (const it of items) {
        const gstAmt = gstAmountFromItem(it);
        const amtWithGst = amountWithGstFromItem(it);

        rows.push({
          orderDate: o.orderDate ? fmtDate(o.orderDate) : "",
          orderNo: o.orderNo || "",
          partyName: o.partyName || "",
          category: it.category || "",
          boardQuality: it.boardQuality || "",
          name: it.name || "",
          quantity: it.quantity,
          rate: it.rate,
          totalWeight: it.totalWeight,
          gstRate: it.gstRate,
          gstAmount: gstAmt.toFixed(2),
          amountWithGst: amtWithGst.toFixed(2),
          totalCost: it.totalCost,
          costPerPcs: it.costPerPcs,
        });
      }
    }

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");
    XLSX.writeFile(workbook, "selected_orders.xlsx");
  };

  const downloadPDF = () => {
    const selectedOrders = getSelectedOrders();
    if (!selectedOrders) return;

    const doc = new jsPDF();

    const headers = [
      [
        "Date",
        "Order No",
        "Party",
        "Board Q.",
        "Product",
        "Qty",
        "Rate",
        "Weight",
        "GST %",
        "GST Amt",
        "Amount (GST)",
      ],
    ];

    const data = [];
    for (const o of selectedOrders) {
      const items = Array.isArray(o.items) ? o.items : [];
      for (const it of items) {
        const gstAmt = gstAmountFromItem(it);
        const amtWithGst = amountWithGstFromItem(it);

        data.push([
          o.orderDate ? fmtDate(o.orderDate) : "—",
          o.orderNo || "—",
          o.partyName || "—",
          it.boardQuality || "—",
          it.name || "—",
          it.quantity ?? "—",
          it.rate ?? "—",
          it.totalWeight ?? "—",
          it.gstRate ?? "—",
          gstAmt.toFixed(2),
          amtWithGst.toFixed(2),
        ]);
      }
    }

    // total (GST included)
    let totalAmount = 0;
    for (const o of selectedOrders) {
      const items = Array.isArray(o.items) ? o.items : [];
      for (const it of items) totalAmount += amountWithGstFromItem(it);
    }

    data.push([
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "TOTAL:",
      totalAmount.toFixed(2),
    ]);

    autoTable(doc, {
      head: headers,
      body: data,
      startY: 20,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [25, 118, 210] },
    });

    doc.save("selected_orders.pdf");
  };

  // ✅ submitted total (GST included)
  const submittedTotal = useMemo(() => {
    let sum = 0;
    for (const o of orders) {
      const items = Array.isArray(o.items) ? o.items : [];
      for (const it of items) sum += amountWithGstFromItem(it);
    }
    return sum.toFixed(2);
  }, [orders]);

  /* --------------------- open edit for submitted item --------------------- */
  const openEditSubmittedItem = (orderDoc, itemIndex) => {
    const it = orderDoc?.items?.[itemIndex];
    if (!orderDoc?._id || !it) return;

    setEditOrderId(orderDoc._id);
    setEditItemIndex(itemIndex);

    setEditHeader({
      orderNo: orderDoc.orderNo || "",
      partyName: orderDoc.partyName || "",
    });

    setEditItemMeta({
      productId: it.productId || "",
      name: it.name || "",
      boardQuality: it.boardQuality || "",
      category: it.category || "",
    });

    setEditForm({
      orderSheetL: it.orderSheetL ?? 0,
      orderSheetB: it.orderSheetB ?? 0,
      orderSheetGsm: it.orderSheetGsm ?? 0,
      quantity: it.quantity ?? 0,
      piecePerSheet: it.piecePerSheet ?? 0,
      rate: it.rate ?? 0,
      printingPer1000: it.printingPer1000 ?? 0,
      plateCharge: it.plateCharge ?? 0,
      cuttingPer1000: it.cuttingPer1000 ?? 0,
      conversionCost: it.conversionCost ?? 0,
      miscCost: it.miscCost ?? 0,
      salePrice: it.salePrice ?? 0,
      gstRate: it.gstRate ?? 0,
    });

    setEditOpen(true);
  };

  const saveSubmittedItemEdit = async () => {
    if (!editOrderId || editItemIndex < 0) return;

    const orderDoc = orders.find((x) => x._id === editOrderId);
    if (!orderDoc) return;

    const items = [...(orderDoc.items || [])];
    const old = items[editItemIndex];
    if (!old) return;

    const out = calculateOutputs(editForm);

    const updatedItem = {
      ...old,
      productId: editItemMeta.productId || old.productId,
      category: editItemMeta.category || old.category,
      boardQuality: editItemMeta.boardQuality || old.boardQuality,
      name: editItemMeta.name || old.name,

      orderSheetL: parsed(editForm.orderSheetL),
      orderSheetB: parsed(editForm.orderSheetB),
      orderSheetGsm: parsed(editForm.orderSheetGsm),
      quantity: parsed(editForm.quantity),
      piecePerSheet: parsed(editForm.piecePerSheet),
      rate: parsed(editForm.rate),
      printingPer1000: parsed(editForm.printingPer1000),
      plateCharge: parsed(editForm.plateCharge),
      cuttingPer1000: parsed(editForm.cuttingPer1000),
      conversionCost: parsed(editForm.conversionCost),
      miscCost: parsed(editForm.miscCost),
      salePrice: parsed(editForm.salePrice),
      gstRate: parsed(editForm.gstRate),

      wtPerSheet: parsed(out.wtPerSheet),
      totalWeight: parsed(out.totalWeight),
      amount: parsed(out.amount),
      totalPrintingCost: parsed(out.totalPrintingCost),
      totalCuttingCharges: parsed(out.totalCuttingCharges),
      miscellaneousCharges: parsed(out.miscellaneousCharges),
      totalConversionCost: parsed(out.totalConversionCost),
      totalCost: parsed(out.totalCost),
      totalQuantity: parsed(out.totalQuantity),
      costPerPcs: parsed(out.costPerPcs),
      gstOnSale: parsed(out.gstOnSale),
      profitPerPc: parsed(out.profitPerPc),
    };

    items[editItemIndex] = updatedItem;

    const totals = (() => {
      let amount = 0,
        totalWeight = 0,
        totalCost = 0;
      for (const it of items) {
        amount += parseFloat(it.amount || 0);
        totalWeight += parseFloat(it.totalWeight || 0);
        totalCost += parseFloat(it.totalCost || 0);
      }
      return { amount, totalWeight, totalCost, itemsCount: items.length };
    })();

    try {
      await axios.put(`${ORDERS_API}/${editOrderId}`, {
        orderNo: editHeader.orderNo,
        partyName: editHeader.partyName,
        orderDate: orderDoc.orderDate || new Date().toISOString(),
        totals,
        items,
      });
    } catch (e) {
      console.error("Order update failed:", e);
    }

    try {
      if (updatedItem.productId) {
        await updateProductMaster(updatedItem.productId, editForm, out);
      }
    } catch (e2) {
      console.error("Product sync failed:", e2);
    }

    setEditOpen(false);
    setEditOrderId(null);
    setEditItemIndex(-1);
    setEditHeader({ orderNo: "", partyName: "" });
    setEditItemMeta({
      productId: "",
      name: "",
      boardQuality: "",
      category: "",
    });
    setEditForm({});
    refreshOrders();
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: UI.pageBg,
        pt: `${HEADER_H}px`,
        pb: `${FOOTER_H}px`,
      }}
    >
      {/* HEADER (FIXED) */}
      <Box
        sx={{
          background: UI.headerBg,
          color: "white",
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              py: 1.6,
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              flexWrap: "wrap",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
              <Typography sx={{ fontWeight: 1000, fontSize: 20 }}>
                Rupioo
              </Typography>
              <Typography
                sx={{ fontWeight: 900, fontSize: 16, color: UI.amazonOrange }}
              >
                Orders
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip
                label={`Submitted: ${orders.length}`}
                sx={{
                  fontWeight: 1000,
                  bgcolor: "rgba(255,255,255,0.12)",
                  color: "white",
                }}
              />
              <Chip
                label={`Submitted Total (GST): ${submittedTotal}`}
                sx={{
                  fontWeight: 1000,
                  bgcolor: "rgba(255,255,255,0.12)",
                  color: "white",
                }}
              />
              <Chip
                label={`Paper Orders: ${papers.length}`}
                sx={{
                  fontWeight: 1000,
                  bgcolor: "rgba(255,255,255,0.12)",
                  color: "white",
                }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 3, pb: 4 }}>
        {/* ---------------- PAPER ORDERS ---------------- */}
        <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }} variant="outlined">
          <Typography sx={{ fontWeight: 1000, mb: 1.2, color: UI.text }}>
            Paper Orders (Draft)
          </Typography>

          {papers.length === 0 ? (
            <Typography sx={{ color: UI.sub }}>
              No paper orders right now.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {papers.map((p) => {
                const isOpen = openPaperId === p.paperId;

                const paperAmountBase = (p.items || []).reduce(
                  (acc, it) => acc + (parseFloat(it.outputs?.amount) || 0),
                  0,
                );

                // GST inclusive for paper display (computed)
                const paperGst = (p.items || []).reduce((acc, it) => {
                  const base = parseFloat(it.outputs?.amount || 0);
                  const gstR = parseFloat(it.formData?.gstRate || 0);
                  return acc + (base * gstR) / 100;
                }, 0);

                const paperAmountWithGst = (paperAmountBase + paperGst).toFixed(
                  2,
                );

                return (
                  <Paper
                    key={p.paperId}
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      borderColor: isOpen ? UI.amazonOrangeDeep : UI.border,
                      overflow: "hidden",
                      background: UI.cardBg,
                    }}
                  >
                    <Box sx={{ height: 6, background: UI.amazonOrangeDeep }} />

                    <Box
                      sx={{
                        p: 1.5,
                        display: "flex",
                        gap: 1.2,
                        flexWrap: "wrap",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1.2,
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <TextField
                          label="Order No."
                          size="small"
                          value={p.orderNo || ""}
                          onChange={(e) =>
                            updatePaper(p.paperId, { orderNo: e.target.value })
                          }
                          sx={{ minWidth: 220 }}
                        />

                        <TextField
                          label="Party Name"
                          size="small"
                          value={p.partyName || ""}
                          onChange={(e) =>
                            updatePaper(p.paperId, {
                              partyName: e.target.value,
                            })
                          }
                          sx={{ minWidth: 260 }}
                        />

                        <Chip
                          label={`Date: ${fmtDate(p.orderDate)}`}
                          sx={{
                            fontWeight: 1000,
                            bgcolor: UI.soft,
                            border: `1px solid ${UI.border}`,
                          }}
                        />

                        <Chip
                          label={`Items: ${(p.items || []).length}`}
                          sx={{
                            fontWeight: 1000,
                            bgcolor: UI.soft,
                            border: `1px solid ${UI.border}`,
                          }}
                        />

                        <Chip
                          label={`Amount (GST): ${paperAmountWithGst}`}
                          sx={{
                            fontWeight: 1000,
                            bgcolor: "#fff",
                            border: `1px solid ${UI.border}`,
                          }}
                        />
                      </Box>

                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setOpenPaperId(isOpen ? null : p.paperId);
                            cancelPaperItemEdit();
                          }}
                          startIcon={
                            isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />
                          }
                          sx={{ fontWeight: 1000, borderRadius: 2 }}
                        >
                          {isOpen ? "Hide" : "View"}
                        </Button>

                        <Button
                          variant="contained"
                          onClick={() => submitPaper(p)}
                          startIcon={<CheckCircleIcon />}
                          sx={{
                            background: UI.amazonOrangeDeep,
                            color: "#111",
                            fontWeight: 1000,
                            borderRadius: 2,
                            "&:hover": { background: UI.amazonOrange },
                          }}
                        >
                          Submit
                        </Button>

                        <Tooltip title="Delete Paper Order" arrow>
                          <IconButton
                            onClick={() => deletePaper(p.paperId)}
                            sx={{
                              border: `1px solid ${UI.border}`,
                              background: "#fff",
                              "&:hover": { background: UI.soft },
                            }}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {isOpen && (
                      <>
                        <Divider />
                        <Box sx={{ p: 1.2 }}>
                          <Typography
                            sx={{ fontWeight: 1000, color: UI.text, mb: 1 }}
                          >
                            Items (Edit before Submit)
                          </Typography>

                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Board Quality</TableCell>
                                <TableCell>Category</TableCell>
                                <TableCell>Product</TableCell>
                                <TableCell>Qty</TableCell>
                                <TableCell>Rate</TableCell>
                                <TableCell>Total Weight</TableCell>
                                <TableCell>GST %</TableCell>
                                <TableCell>GST Amt</TableCell>
                                <TableCell>Amount (GST)</TableCell>
                                <TableCell align="right">Edit</TableCell>
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {(p.items || []).map((it, idx) => {
                                const base = parseFloat(
                                  it.outputs?.amount || 0,
                                );
                                const gstR = parseFloat(
                                  it.formData?.gstRate || 0,
                                );
                                const gstAmt = (base * gstR) / 100;
                                const amtWithGst = base + gstAmt;

                                return (
                                  <TableRow key={it.key || idx}>
                                    <TableCell>{it.boardQuality}</TableCell>
                                    <TableCell>
                                      {it.category ||
                                        it.catageory ||
                                        it.Catageory ||
                                        "—"}
                                    </TableCell>
                                    <TableCell>{it.name}</TableCell>
                                    <TableCell>
                                      {it.formData?.quantity}
                                    </TableCell>
                                    <TableCell>{it.formData?.rate}</TableCell>
                                    <TableCell>
                                      {it.outputs?.totalWeight}
                                    </TableCell>
                                    <TableCell>
                                      {it.formData?.gstRate}
                                    </TableCell>
                                    <TableCell>{gstAmt.toFixed(2)}</TableCell>
                                    <TableCell>
                                      {amtWithGst.toFixed(2)}
                                    </TableCell>

                                    <TableCell align="right">
                                      <Tooltip title="Edit before submit" arrow>
                                        <IconButton
                                          onClick={() =>
                                            startPaperItemEdit(p.paperId, idx)
                                          }
                                        >
                                          <EditIcon />
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}

                              <TableRow>
                                <TableCell colSpan={7} />
                                <TableCell sx={{ fontWeight: 1000 }}>
                                  TOTAL
                                </TableCell>
                                <TableCell sx={{ fontWeight: 1000 }}>
                                  {paperAmountWithGst}
                                </TableCell>
                                <TableCell />
                              </TableRow>
                            </TableBody>
                          </Table>

                          {paperEdit && paperEdit.paperId === p.paperId && (
                            <Box sx={{ mt: 2 }}>
                              <Paper
                                variant="outlined"
                                sx={{
                                  borderRadius: 2,
                                  borderColor: UI.amazonOrangeDeep,
                                  background: UI.soft,
                                  overflow: "hidden",
                                }}
                              >
                                <Box
                                  sx={{
                                    height: 6,
                                    background: UI.amazonOrangeDeep,
                                  }}
                                />

                                <Box
                                  sx={{
                                    p: 1.6,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <Typography
                                    sx={{ fontWeight: 1000, color: UI.text }}
                                  >
                                    Edit Draft Item #{paperEdit.idx + 1}
                                  </Typography>

                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <Button
                                      variant="contained"
                                      onClick={savePaperItemEdit}
                                      sx={{
                                        background: UI.amazonOrangeDeep,
                                        color: "#111",
                                        fontWeight: 1000,
                                        borderRadius: 2,
                                        "&:hover": {
                                          background: UI.amazonOrange,
                                        },
                                      }}
                                    >
                                      Save
                                    </Button>

                                    <Button
                                      variant="outlined"
                                      onClick={cancelPaperItemEdit}
                                      sx={{ fontWeight: 1000, borderRadius: 2 }}
                                      startIcon={<CloseIcon />}
                                    >
                                      Cancel
                                    </Button>
                                  </Box>
                                </Box>

                                <Divider />

                                <Box sx={{ p: 1.6 }}>
                                  <RowFields
                                    title="Inputs (Edit)"
                                    fields={INPUT_FIELDS}
                                    values={paperEditForm}
                                    onChange={(k, v) =>
                                      setPaperEditForm((prev) => ({
                                        ...prev,
                                        [k]: isNaN(v) ? v : parseFloat(v),
                                      }))
                                    }
                                  />

                                  <Divider sx={{ my: 2 }} />

                                  <RowFields
                                    title="Outputs (Auto)"
                                    fields={OUTPUT_FIELDS}
                                    values={paperEditOutputs}
                                    readOnly
                                    onChange={() => {}}
                                  />
                                </Box>
                              </Paper>
                            </Box>
                          )}
                        </Box>
                      </>
                    )}
                  </Paper>
                );
              })}
            </Box>
          )}
        </Paper>

        {/* ---------------- SUBMITTED ORDERS ---------------- */}
        <Paper sx={{ mt: 2, p: 2, borderRadius: 2 }} variant="outlined">
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            <Button
              onClick={downloadExcel}
              variant="contained"
              sx={{
                background: "#4caf50",
                fontWeight: 900,
                borderRadius: 2,
                "&:hover": { background: "#3e8e41" },
              }}
            >
              📥 Download Excel (Selected)
            </Button>

            <Button
              onClick={downloadPDF}
              variant="contained"
              sx={{
                background: "#f44336",
                fontWeight: 900,
                borderRadius: 2,
                "&:hover": { background: "#d32f2f" },
              }}
            >
              🧾 Download PDF (Selected)
            </Button>

            {selectedIds.length > 0 && (
              <Button
                onClick={deleteSelectedSubmitted}
                variant="contained"
                sx={{
                  background: "#B12704",
                  fontWeight: 900,
                  borderRadius: 2,
                  "&:hover": { background: "#8f1f03" },
                }}
              >
                Delete Selected ({selectedIds.length})
              </Button>
            )}
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 1000, mb: 1 }}>
            Submitted Orders (Saved Forever)
          </Typography>

          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Date</TableCell>
                <TableCell>Order No.</TableCell>
                <TableCell>Party</TableCell>
                <TableCell>Items</TableCell>
                <TableCell>Total Weight</TableCell>
                <TableCell>Total Cost</TableCell>
                <TableCell>Amount (GST)</TableCell>
                <TableCell align="right">View</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {orders.map((o) => {
                const isOpen = openOrderId === o._id;

                const items = Array.isArray(o.items) ? o.items : [];
                const totalWeight = items.reduce(
                  (a, it) => a + parsed(it.totalWeight),
                  0,
                );
                const totalCost = items.reduce(
                  (a, it) => a + parsed(it.totalCost),
                  0,
                );
                const totalAmountWithGst = items.reduce(
                  (a, it) => a + amountWithGstFromItem(it),
                  0,
                );

                return (
                  <React.Fragment key={o._id}>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(o._id)}
                          onChange={() => {
                            setSelectedIds((prev) =>
                              prev.includes(o._id)
                                ? prev.filter((id) => id !== o._id)
                                : [...prev, o._id],
                            );
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        {o.orderDate ? fmtDate(o.orderDate) : "—"}
                      </TableCell>

                      <TableCell
                        sx={{
                          cursor: "pointer",
                          color: UI.linkBlue,
                          fontWeight: 900,
                        }}
                        onClick={() => setOpenOrderId(isOpen ? null : o._id)}
                      >
                        {o.orderNo || "—"}
                      </TableCell>

                      <TableCell>{o.partyName || "—"}</TableCell>

                      <TableCell>{items.length}</TableCell>
                      <TableCell>{totalWeight.toFixed(2)}</TableCell>
                      <TableCell>{totalCost.toFixed(2)}</TableCell>
                      <TableCell sx={{ fontWeight: 1000, color: "#B12704" }}>
                        {totalAmountWithGst.toFixed(2)}
                      </TableCell>

                      <TableCell align="right">
                        <Tooltip title={isOpen ? "Hide" : "View Items"} arrow>
                          <IconButton
                            onClick={() =>
                              setOpenOrderId(isOpen ? null : o._id)
                            }
                          >
                            {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {isOpen && (
                      <TableRow>
                        <TableCell colSpan={9} sx={{ background: UI.soft }}>
                          <Paper
                            variant="outlined"
                            sx={{
                              borderRadius: 2,
                              borderColor: UI.amazonOrangeDeep,
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                height: 6,
                                background: UI.amazonOrangeDeep,
                              }}
                            />
                            <Box sx={{ p: 1.3 }}>
                              <Typography sx={{ fontWeight: 1000, mb: 1 }}>
                                Items in this Order
                              </Typography>

                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Board Quality</TableCell>
                                    <TableCell>Category</TableCell>
                                    <TableCell>Product</TableCell>
                                    <TableCell>Qty</TableCell>
                                    <TableCell>Rate</TableCell>
                                    <TableCell>Total Weight</TableCell>
                                    <TableCell>GST %</TableCell>
                                    <TableCell>GST Amt</TableCell>
                                    <TableCell>Amount (GST)</TableCell>
                                    <TableCell align="right">Edit</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {items.map((it, idx) => {
                                    const gstAmt = gstAmountFromItem(it);
                                    const amtWithGst =
                                      amountWithGstFromItem(it);

                                    return (
                                      <TableRow key={idx}>
                                        <TableCell>{it.boardQuality}</TableCell>
                                        <TableCell>
                                          {it.category || "—"}
                                        </TableCell>
                                        <TableCell>{it.name}</TableCell>
                                        <TableCell>{it.quantity}</TableCell>
                                        <TableCell>{it.rate}</TableCell>
                                        <TableCell>{it.totalWeight}</TableCell>
                                        <TableCell>{it.gstRate}</TableCell>
                                        <TableCell>
                                          {gstAmt.toFixed(2)}
                                        </TableCell>
                                        <TableCell>
                                          {amtWithGst.toFixed(2)}
                                        </TableCell>
                                        <TableCell align="right">
                                          <Tooltip title="Edit this item" arrow>
                                            <IconButton
                                              onClick={() =>
                                                openEditSubmittedItem(o, idx)
                                              }
                                            >
                                              <EditIcon />
                                            </IconButton>
                                          </Tooltip>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </Box>
                          </Paper>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}

              <TableRow>
                <TableCell colSpan={6} />
                <TableCell sx={{ fontWeight: 1000 }}>Total (GST):</TableCell>
                <TableCell sx={{ fontWeight: 1000, color: "#B12704" }}>
                  {submittedTotal}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      </Container>

      {/* FOOTER (FIXED) */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          height: `${FOOTER_H}px`,
          background: UI.headerBg,
          color: "white",
          zIndex: 1300,
          display: "flex",
          alignItems: "center",
          borderTop: "1px solid rgba(255,255,255,0.12)",
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography sx={{ fontWeight: 900, fontSize: 13 }}>
              Rupioo Orders
            </Typography>

            <Typography sx={{ fontWeight: 900, fontSize: 13, opacity: 0.9 }}>
              Selected: {selectedIds.length}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ---------------- SUBMITTED ITEM EDIT DIALOG ---------------- */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 1000 }}>
          Edit Submitted Order Item (updates order + updates Home product too)
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: "flex", gap: 1.2, flexWrap: "wrap", mb: 1.5 }}>
            <TextField
              label="Order No."
              value={editHeader.orderNo}
              onChange={(e) =>
                setEditHeader((p) => ({ ...p, orderNo: e.target.value }))
              }
              sx={{ minWidth: 220 }}
            />
            <TextField
              label="Party Name"
              value={editHeader.partyName}
              onChange={(e) =>
                setEditHeader((p) => ({ ...p, partyName: e.target.value }))
              }
              sx={{ minWidth: 260 }}
            />

            <Chip
              label={`Product: ${editItemMeta.name || "—"}`}
              sx={{
                fontWeight: 1000,
                bgcolor: UI.soft,
                border: `1px solid ${UI.border}`,
              }}
            />
          </Box>

          <RowFields
            title="Inputs (Edit)"
            fields={INPUT_FIELDS}
            values={editForm}
            onChange={(k, v) =>
              setEditForm((prev) => ({
                ...prev,
                [k]: isNaN(v) ? v : parseFloat(v),
              }))
            }
          />

          <Divider sx={{ my: 2 }} />

          <RowFields
            title="Outputs (Auto)"
            fields={OUTPUT_FIELDS}
            values={editOutputs}
            readOnly
            onChange={() => {}}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setEditOpen(false);
              setEditOrderId(null);
              setEditItemIndex(-1);
              setEditHeader({ orderNo: "", partyName: "" });
              setEditItemMeta({
                productId: "",
                name: "",
                boardQuality: "",
                category: "",
              });
              setEditForm({});
            }}
            startIcon={<CloseIcon />}
            sx={{ fontWeight: 1000 }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={saveSubmittedItemEdit}
            sx={{
              background: UI.amazonOrangeDeep,
              color: "#111",
              fontWeight: 1000,
              "&:hover": { background: UI.amazonOrange },
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
